import { AiFillInfoCircle } from 'react-icons/ai';
import ProgressBar from '@ramonak/react-progress-bar';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { backendErrorNotification } from '../../../../components/Notification';
import { ActionButtonsContainer } from '../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../enums/httpStatus';
import { MapIcon, CalendarScheduleIcon, RouteIcon } from '../../../../icons';
import { visitRoutineSellerSummaryAPI } from '../../../../services/requests';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { SellerVisitRoutineSummary } from '../../../../types';
import { ApiResPaginated } from '../../../../types/apiResponseTypes';

import AsyncTable, { Column, RequestPage } from '../../../../components/AsyncTable';
import Btn from '../../../../components/Buttons/Btn';
import {
  getLocalizedHelpCenterMeta,
  HelpCenterModal,
} from '../../../../pages/HelpCenter';
import {
  VISIT_SCHEDULED_GUIDE_SLUG,
} from '../../../../pages/HelpCenter/content/visitScheduled';
import { getHelpCenterContentBySlug } from '../../../../pages/HelpCenter/registry';
import ModalReconcileRoutines from './Components/ModalReconcileRoutines';
import ModalGenerateScheduledVisits from './Components/ModalGenerateScheduledVisits';
import ModalManageRoutines from './Components/ModalManageRoutines';
import ModalVisitCalendar from './Components/ModalVisitCalendar';

const VisitRoutinesPage = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [sellerVisitRoutineSummaries, setSellerVisitRoutineSummaries] =
    useState<SellerVisitRoutineSummary[]>([]);

  const [showModalManage, setModalManage] = useState(false);
  const [showModalScheduledVisits, setShowModalScheduledVisits] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SellerVisitRoutineSummary | null>(null);
  const [generateTarget, setGenerateTarget] = useState<{
    all_sellers: boolean;
    CodigoVendedor?: string;
    Nome?: string;
  } | null>(null);
  const [updateTable, setUpdateTable] = useState(0);
  const [showModalReconcile, setShowModalReconcile] = useState(false);
  const [activeHelpSlug, setActiveHelpSlug] = useState<string | null>(null);

  const visitScheduleHelpContent = getHelpCenterContentBySlug(
    VISIT_SCHEDULED_GUIDE_SLUG,
    'modal',
  );
  const visitScheduleHelpTitle = visitScheduleHelpContent
    ? getLocalizedHelpCenterMeta(
      visitScheduleHelpContent,
      i18n.resolvedLanguage ?? i18n.language,
    ).title
    : null;

  const getItems: RequestPage = async ({ search, page, quantityPerPage }) => {
    let hasMore = true;
    const source = visitRoutineSellerSummaryAPI.axios.CancelToken.source();

    try {
      // eslint-disable-next-line max-len
      visitRoutineSellerSummaryAPI.query = `?search=${search}&page=${page}&quantityPerPage=${quantityPerPage}`;
      const res = await visitRoutineSellerSummaryAPI.index(source.token);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      const { data, paginated }: ApiResPaginated<SellerVisitRoutineSummary[]> = res.data;

      if (data.length === 0) {
        hasMore = false;
      }

      return {
        data,
        hasMore,
        totalPage: paginated.totalPage,
      };
    } catch (err) {
      if (!visitRoutineSellerSummaryAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    }
  };

  const handleSellerColumn = (row: SellerVisitRoutineSummary) => {
    const { CodigoVendedor } = row;
    const { Nome } = row;
    return <p>{`${CodigoVendedor} - ${Nome}`}</p>;
  };

  const handlePercentColumn = (row: SellerVisitRoutineSummary) => {
    const { visit_routines_count } = row;
    const { active_customers_count } = row;
    const percent = (
      Number(visit_routines_count) / Number(active_customers_count)
    ) * 100;

    if (!Number.isFinite(percent)) {
      return null;
    }

    const normalizedPercent = Math.min(percent, 100);
    const formattedPercent = `${normalizedPercent.toFixed(2)}%`;

    return (
      <div style={{ position: 'relative', minWidth: '8rem' }}>
        <ProgressBar
          completed={normalizedPercent.toFixed(2)}
          bgColor={theme.colors.positive}
          baseBgColor={theme.colors.onBackground + 50}
          labelColor="transparent"
        />

        <span
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            fontSize: '0.75rem',
            fontWeight: 700,
            lineHeight: 1,
            color: theme.colors.text,
            textShadow: '0 0 2px rgba(255, 255, 255, 0.8)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formattedPercent}
        </span>
      </div>
    );
  };

  const handleEditBtn = (row: SellerVisitRoutineSummary) => {
    setSelectedItem(row);
    setModalManage(true);
  };

  const handleScheduledVisitsBtn = (row: SellerVisitRoutineSummary) => {
    setSelectedItem(row);
    setShowModalScheduledVisits(true);
  };

  const handleGenerateSellerSchedules = (row: SellerVisitRoutineSummary) => {
    setGenerateTarget({
      all_sellers: false,
      CodigoVendedor: row.CodigoVendedor,
      Nome: row.Nome,
    });
  };

  const actionsButtons = (row: SellerVisitRoutineSummary) => (
    <ActionButtonsContainer type={1}>
      <MapIcon
        size="1.5rem"
        title={t('generateVisitSchedulesForSeller') as string}
        style={{ cursor: 'pointer', margin: '0 1rem' }}
        onClick={() => handleGenerateSellerSchedules(row)}
      />
      <CalendarScheduleIcon
        size="1.5rem"
        title={t('sellerAppointments') as string}
        style={{ cursor: 'pointer', marginRight: '1rem' }}
        onClick={() => handleScheduledVisitsBtn(row)}
      />
      <RouteIcon
        size="1.5rem"
        title={t('visitScheduleRoutine') as string}
        onClick={() => handleEditBtn(row)}
      />
    </ActionButtonsContainer>
  );

  const columns: Column<SellerVisitRoutineSummary>[] = [
    {
      label: t('seller') as string,
      accessor: 'CodigoVendedor',
      Cell: handleSellerColumn,
    },
    {
      label: t('totalScheduledCustomers') as string,
      accessor: 'visit_routines_count',
    },
    {
      label: t('TotalCustomersInThePortfolio') as string,
      accessor: 'active_customers_count',
    },
    {
      label: `% ${t('scheduling')}`,
      Cell: handlePercentColumn,
    },
    {
      label: '',
      Cell: actionsButtons,
    },
  ];

  const callbackToManagerSchedules = () => {
    setSelectedItem(null);
    setUpdateTable(updateTable + 1);
  };

  const callbackToGenerateSchedules = () => {
    setGenerateTarget(null);
    setUpdateTable(updateTable + 1);
  };

  const openHelpModal = () => {
    setActiveHelpSlug(VISIT_SCHEDULED_GUIDE_SLUG);
  };

  const closeHelpModal = () => {
    setActiveHelpSlug(null);
  };

  return (
    <ContentContainer
      title={t('visitSchedule')}
      titleAddon={visitScheduleHelpContent ? (
        <AiFillInfoCircle
          title={visitScheduleHelpTitle ?? undefined}
          onClick={openHelpModal}
          style={{
            color: theme.colors.warning,
            cursor: 'pointer',
            fontSize: '1.05rem',
            marginTop: '0.1rem',
          }}
        />
      ) : null}
    >
      {activeHelpSlug ? (
        <HelpCenterModal
          showModal={Boolean(activeHelpSlug)}
          closeModal={closeHelpModal}
          slug={activeHelpSlug}
          maximized
        />
      ) : null}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <Btn
          onClick={() => setModalManage(true)}
          style={{ marginLeft: '0', marginBottom: '0' }}
          text={t('manageSchedules')}
        />

        <Btn
          onClick={() => setGenerateTarget({ all_sellers: true })}
          style={{ marginLeft: '0', marginBottom: '0' }}
          text={t('generateVisitSchedulesForAllSellers')}
        />

        <Btn
          onClick={() => setShowModalReconcile(true)}
          style={{ marginLeft: '0', marginBottom: '0' }}
          text={t('visitReconcileRoutines')}
        />
      </div>

      {showModalManage && (
        <ModalManageRoutines
          closeModal={() => setModalManage(false)}
          selectedItem={selectedItem}
          callback={callbackToManagerSchedules}
        />
      )}

      {showModalScheduledVisits && (
        <ModalVisitCalendar
          closeModal={() => setShowModalScheduledVisits(false)}
          selectedItem={selectedItem}
          callback={callbackToManagerSchedules}
        />
      )}

      {generateTarget && (
        <ModalGenerateScheduledVisits
          closeModal={() => setGenerateTarget(null)}
          target={generateTarget}
          callback={callbackToGenerateSchedules}
        />
      )}

      {showModalReconcile && (
        <ModalReconcileRoutines
          closeModal={() => setShowModalReconcile(false)}
          callback={() => setUpdateTable(updateTable + 1)}
        />
      )}

      <AsyncTable
        tableName={t('visitSchedule')}
        columns={columns}
        value={sellerVisitRoutineSummaries}
        onChange={setSellerVisitRoutineSummaries}
        requestPage={getItems}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
            downloadBtns: { marginLeft: '0.5rem' },
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
          hideDownloads: true,
        }}
      />
    </ContentContainer>
  );
};

export default VisitRoutinesPage;
